import type {Dispatch, GetState} from 'src/reducers';
import type {TargetEntity} from 'src/types/target-entity-types';

import * as React from 'react';
import 'redux';
import {useDispatch, useSelector} from 'react-redux';
import invariant from 'invariant';

import {getCurrentWorkflowEntityType} from 'src/selectors/workflow';
import {
  getEntityTypeMappings,
  selectCandidateEntityType,
} from 'src/selectors/ats-entities';
import {
  getSearchableEntity,
  getSearchableEntityFromStandardEntities,
} from 'src/utils/entities';
import {useApi} from 'src/hooks/useApi';

import {
  fetchPersonRelatedEntities,
  getEntityVariableValues,
} from 'src/action-creators/audience-members/multientity';

import {selectRelatedEntitiesAsSummaries} from 'src/selectors/multientity-audience';
import {selectEntityMappingsByEntityType} from 'src/selectors/target-entity-types';


const getPlacementsAction =
  (
    audienceMemberId: string | null | undefined,
    entityType: string | null | undefined,
    personEntityType: string,
  ) =>
  async (dispatch: Dispatch, getState: GetState) => {
    // TODO (kyle): verify that this will work with just the related entityType
    await dispatch(
      fetchPersonRelatedEntities(
        // @ts-expect-error - TS2345 - Argument of type 'string | null | undefined' is not assignable to parameter of type 'string'.
        audienceMemberId,
        entityType,
        personEntityType,
      ),
    );

    return selectRelatedEntitiesAsSummaries(
      getState(),
      // @ts-expect-error - TS2554 - Expected 1 arguments, but got 3.
      audienceMemberId,
      entityType,
    );
  };

export function useMockedEntities(
  audienceMemberId: string | null,
  selectedPlacementId: string | null,
  entityType: string,
): {
  loading: boolean;
  mockedEntityId: string | null | undefined;
  relatedEntities: undefined;
} {
  const workflowEntityType = useSelector(getCurrentWorkflowEntityType);
  const dispatch = useDispatch();

  const getEntityVarValues = React.useCallback(
    // @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type. | TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
    (...args) => dispatch(getEntityVariableValues(...args)),
    [dispatch],
  );
  const getMEPlacements = React.useCallback(
    (audienceMemberId: string, entityType: string, personEntityType: string) =>
      dispatch(
        getPlacementsAction(audienceMemberId, entityType, personEntityType),
      ),
    [dispatch],
  );

  const atsEntityMappings = useSelector(getEntityTypeMappings);
  // @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.
  const entityMapping = atsEntityMappings[entityType];
  const workflowEntityInfo = useSelector(getCurrentWorkflowEntityType);
  const searchableEntityType = getSearchableEntity(
    atsEntityMappings,
    // @ts-expect-error - TS2345 - Argument of type 'AtsEntity | null | undefined' is not assignable to parameter of type 'AtsEntity'.
    workflowEntityInfo,
  )?.name;

  async function getMeRelatedEntities(
    audienceMemberId: string,
    entityType: string,
  ) {
    invariant(
      searchableEntityType,
      'Called getMeRelatedEntities without an searchableEntityType.',
    );

    const relatedEntities = await getMEPlacements(
      audienceMemberId,
      entityType,
      searchableEntityType,
    );

    //fetch the entity variables for the first entity (default selected in dropdown)
    if (relatedEntities?.length) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '0' can't be used to index type '(dispatch: Dispatch, getState: GetState) => Promise<string[]>'.
      await getEntityVarValues(relatedEntities[0].id, workflowEntityType?.name);
    }
    const selectedEntityId = relatedEntities.length
      ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '0' can't be used to index type '(dispatch: Dispatch, getState: GetState) => Promise<string[]>'.
        relatedEntities[0].id
      : null;
    return [relatedEntities, selectedEntityId];
  }

  const [mockedEntityId, setMockedEntityId] = React.useState<any>();
  const [loading, setLoading] = React.useState(false);
  const [relatedEntities, setRelatedEntities] = React.useState<any>();

  React.useEffect(() => {
    // don't run if no audienceMemberId
    if (!audienceMemberId) {
      return;
    }
    setLoading(true);
    setMockedEntityId(audienceMemberId);

    if (entityMapping?.is_person) {
      getEntityVarValues(audienceMemberId, workflowEntityType?.name).then(
        () => {
          setMockedEntityId(audienceMemberId);
          setLoading(false);
        },
      );
    } else {
      getMeRelatedEntities(audienceMemberId, entityType).then(
        // @ts-expect-error - TS2345 - Argument of type '([relatedEntities, selectedEntityId]: [any, any]) => void' is not assignable to parameter of type '(value: any[]) => void | PromiseLike<void>'.
        ([relatedEntities, selectedEntityId]: [any, any]) => {
          setRelatedEntities(relatedEntities);
          setMockedEntityId(selectedEntityId);
          setLoading(false);
        },
      );
    }
  }, [audienceMemberId, entityType]);

  React.useEffect(() => {
    if (selectedPlacementId) {
      setMockedEntityId(selectedPlacementId);
      getEntityVarValues(selectedPlacementId, workflowEntityType?.name);
    }
  }, [selectedPlacementId]);

  return {loading, mockedEntityId, relatedEntities};
}

/** this function hits the api for candidate typeahead search,
 * gets the list of candidates and returns first candidate from
 * that list.
 */
export const useCandidateForEntity = (
  entityType: string,
):
  | {
      id: string;
    }
  | null
  | undefined => {
  const candidateEntityType = useSelector(selectCandidateEntityType);
  const result = useApi(
    entityType === candidateEntityType
      ? `typeahead/entities/${entityType}?limit=5&offset=0&search_string=`
      : '',
  ).result;
  // @ts-expect-error - TS2339 - Property 'entities' does not exist on type '{}'. | TS2339 - Property 'entities' does not exist on type '{}'. | TS2339 - Property 'entities' does not exist on type '{}'.
  return result?.entities && result?.entities.length > 0 && result?.entities[0];
};

export const useRandomPersonEntity = (
  entity: TargetEntity,
  recipientEntityType?: string | null,
):
  | {
      [key: string]: string | number | boolean;
    }
  | null
  | undefined => {
  /*Note(Vish): We need to get searchable entity type and pick entity id of related person entity type for non searchable entity type,
   * Otherwise variables won't show for non person entity types from the preview API. This changes also needs to be done in beefree preview.
   * This can be taken up once Backend provides us with the variable value getter API.
   * */
  const entityMappings = useSelector(selectEntityMappingsByEntityType);
  const searchableEntity = getSearchableEntityFromStandardEntities(
    entity,
    entityMappings,
    recipientEntityType,
  );
  let url = '';
  if (
    searchableEntity?.entity_type &&
    searchableEntity?.entity_type === entity.entity_type
  ) {
    url = `typeahead/entities/${searchableEntity.entity_type}?limit=5&offset=0&search_string=`;
  }
  const result = useApi(url).result;
  if (url === '') {
    return {id: 'simulate'};
  }
  // @ts-expect-error - TS2339 - Property 'entities' does not exist on type '{}'. | TS2339 - Property 'entities' does not exist on type '{}'.
  return result && result?.entities && result?.entities.length > 0
    ? // @ts-expect-error - TS2339 - Property 'entities' does not exist on type '{}'.
      result.entities[0]
    : null;
};
